import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { ApiHotel } from '@ibe/api';
import { ServiceSearchParams } from '@ibe/components';
import { OfferBaseData } from '../Models/CMS/supermodels/OfferBaseData';
import { CmsOfferPackage } from '../Models/CMS/CmsOfferPackage';
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

abstract class OffersUtil {
  public static getDayRelevantOffers<T extends OfferBaseData>(date: Date, offers?: T[]): T[] {
    const result: T[] = [];

    if (!offers?.length) {
      return result;
    }

    const testedDate = dayjs(date).startOf('day');

    offers
      .filter(offer => offer.startDate && offer.endDate)
      .forEach(offer => {
        const offsetDate = dayjs(Date.now()).local().add(offer.offsetDays, 'day').startOf('day');

        const offerStart = dayjs(offer.startDate as string)
          .local()
          .startOf('day');
        const offerEnd = dayjs(offer.endDate as string)
          .local()
          .startOf('day');

        if (testedDate.isBetween(offerStart, offerEnd, null, '[]')) {
          if (testedDate.isSameOrBefore(offsetDate)) {
            return;
          }
          result.push(offer);
        }
      });
    return result;
  }

  public static getMonthRelevantOffers<T extends OfferBaseData>(month?: string, offers?: T[]): T[] {
    const result: T[] = [];

    if (!offers?.length || !month) {
      return result;
    }

    const testedMonth = dayjs(month);

    return offers
      .filter(offer => offer.startDate && offer.endDate)
      .filter(offer => {
        const start = dayjs(offer.startDate as string);
        const end = dayjs(offer.endDate as string);

        const monthStart = testedMonth.startOf('month');
        const monthEnd = testedMonth.endOf('month');

        return end.isSameOrAfter(monthStart) && start.isSameOrBefore(monthEnd);
      });
  }

  public static getDealFinderRelatedOffers<T extends OfferBaseData>(
    offers: T[],
    packageCode?: string,
    hotelCode?: string
  ): T[] {
    if (!offers.length) {
      return [];
    }

    if (!packageCode || !hotelCode) {
      return [];
    }

    return this.getOffersForPackagesAndHotelCodes(offers, packageCode, hotelCode);
  }

  public static getTopPriorityOffer<T extends OfferBaseData>(offers: T[]): T | null {
    if (!offers.length) {
      return null;
    }

    const minPriority = Math.min(...offers.map(p => p.priority ?? 0));
    const offersWithMinPriority = offers.filter(p => p.priority === minPriority);

    if (offersWithMinPriority.length > 1 || offersWithMinPriority.length < 1) {
      return null;
    }

    return offersWithMinPriority[0];
  }

  public static getOfferWithPillInformation<T extends OfferBaseData>(
    bannerOffer: T | undefined,
    packageCode: string | undefined | null,
    hotelModel: ApiHotel | undefined
  ): T | undefined {
    if (bannerOffer && hotelModel && packageCode) {
      const filteringResult = this.getOffersForPackagesAndHotelCodes(
        [bannerOffer],
        packageCode,
        hotelModel.code
      );

      if (
        filteringResult?.length
        // filteringResult[0]?.offerPillAndCartBanner?.characteristicGroupCode
      ) {
        return bannerOffer;
      }
    }
  }

  public static getBannerOffer<T extends OfferBaseData>(
    offers?: T[],
    searchParams?: Partial<ServiceSearchParams>
  ): T | undefined {
    if (offers?.length && searchParams?.startDate && searchParams.endDate && searchParams.code) {
      const testedDate = dayjs(searchParams.startDate).startOf('day');

      const packageRelatedOffers = this.getOffersForPackagesAndHotelCodes(
        offers,
        searchParams.code
      );

      const bannerOffer = packageRelatedOffers
        .filter(offer => offer.startDate && offer.endDate)
        .filter(offer => {
          const offsetDate = dayjs(Date.now()).local().add(offer.offsetDays, 'day').startOf('day');

          const offerStart = dayjs(offer.startDate as string)
            .local()
            .startOf('day');
          const offerEnd = dayjs(offer.endDate as string)
            .local()
            .startOf('day');

          if (testedDate.isBetween(offerStart, offerEnd, null, '[]')) {
            return !testedDate.isSameOrBefore(offsetDate);
          }
        })
        .reduce<T | undefined>((best, current) => {
          if (best === undefined) return current;
          if (current.priority === undefined) return best;
          if (best.priority === undefined) return current;
          return current.priority < best.priority ? current : best;
        }, undefined);

      return bannerOffer;
    }

    return undefined;
  }

  public static getOffersForPackagesAndHotelCodes<T extends OfferBaseData>(
    offers: T[],
    packageCode: string,
    hotelCode?: string
  ): T[] {
    return offers.filter(offer =>
      offer.packages?.some(offerPackage =>
        this.offerPredicateByPackageAndHotelCodes(offerPackage, packageCode, hotelCode)
      )
    );
  }

  private static offerPredicateByPackageAndHotelCodes(
    offerPackage: CmsOfferPackage,
    packageCode: string,
    hotelCode: string | undefined
  ): boolean {
    const isPackageMatch = offerPackage.packageCode === packageCode;

    if (!hotelCode) {
      return isPackageMatch;
    }

    const isHotelCodeIncluded =
      !offerPackage.hotelsCodes ||
      offerPackage?.hotelsCodes?.length === 0 ||
      offerPackage?.hotelsCodes?.includes(hotelCode);

    return !!(isPackageMatch && isHotelCodeIncluded);
  }
}

export default OffersUtil;
